/** "2 hours ago", the way the artboards word it. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.round((then - Date.now()) / 1000);
  const units: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return formatter.format(Math.round(seconds), "second");
}

/** m:ss, for the pairing code's countdown. */
export function countdown(seconds: number): string {
  const clamped = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(clamped / 60);
  return `${minutes}:${String(clamped % 60).padStart(2, "0")}`;
}

export function kilobytes(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The deep link the phone understands. */
export function subscribeDeepLink(bundleUrl: string, accessCode: string | null | undefined): string {
  const url = `diditakeit://subscribe?url=${encodeURIComponent(bundleUrl)}`;
  return accessCode ? `${url}&code=${encodeURIComponent(accessCode)}` : url;
}

/** The shareable https link: the code rides in the fragment, never the query. */
export function shareLink(bundleUrl: string, accessCode: string | null | undefined): string {
  return accessCode ? `${bundleUrl}#code=${encodeURIComponent(accessCode)}` : bundleUrl;
}

/** Reads `#code=…` without ever writing it back into the URL or the title. */
export function accessCodeFromFragment(hash: string): string | null {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  for (const part of fragment.split("&")) {
    const [key, value] = part.split("=");
    if (key === "code" && value) return decodeURIComponent(value);
  }
  return null;
}

/** "12 Aug" — the artboards' date format for a device or session row. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * "in 29 days" for a session's expiry. `relativeTime` would say the same thing,
 * but reading a countdown as "29 days ago" if a clock is off by a second is
 * worse than rounding down to the day.
 */
export function expiresIn(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.floor((then - Date.now()) / 86_400_000);
  if (days < 0) return "expired";
  if (days === 0) return "expires today";
  return `expires in ${days} ${days === 1 ? "day" : "days"}`;
}
